import { useEffect, useRef, useState } from "react";

/**
 * The Orchestrator — a refined version of the earlier radial observatory.
 * One scenario animates at a time with clear labels. A side log grounds
 * the visual in "what is actually happening right now."
 *
 * Signals flow from top-arc source nodes into a central hexagonal core,
 * then out to bottom-arc destination nodes. No invented company or rep names.
 */
// 10 signals the market gives off — angles spread across the top arc.
const SOURCES = [
  { id: "fund",    label: "Funding round",        angle: -170, color: "#00F0FF" },
  { id: "champ",   label: "Champion moved",       angle: -152, color: "#FF7B5B" },
  { id: "pricing", label: "Pricing page visit",   angle: -134, color: "#CCFF00" },
  { id: "tech",    label: "Tech install",         angle: -116, color: "#D946EF" },
  { id: "hire",    label: "Hiring spike",         angle: -98,  color: "#007AFF" },
  { id: "form",    label: "Form submitted",       angle: -80,  color: "#CCFF00" },
  { id: "churn",   label: "Churn signal",         angle: -62,  color: "#FF7B5B" },
  { id: "g2",      label: "G2 review left",       angle: -44,  color: "#00F0FF" },
  { id: "job",     label: "Job change",           angle: -26,  color: "#FFB800" },
  { id: "revisit", label: "Website return visit", angle: -8,   color: "#D946EF" },
];

// 6 destinations = where in the stack the signal lands.
const DESTINATIONS = [
  { id: "rep",      label: "Rep inbox",   angle: 15,  color: "#CCFF00" },
  { id: "slack",    label: "Slack",       angle: 45,  color: "#007AFF" },
  { id: "outreach", label: "Outreach",    angle: 75,  color: "#FFB800" },
  { id: "crm",      label: "CRM",         angle: 105, color: "#00F0FF" },
  { id: "router",   label: "Lead router", angle: 135, color: "#FF7B5B" },
  { id: "cs",       label: "CS team",     angle: 165, color: "#D946EF" },
];

// Signal → Dispatch → Outcome triples (verbatim from user spec).
const ROUTES = [
  { from: "fund",    to: "rep",      outcome: "Sequence triggered" },
  { from: "champ",   to: "slack",    outcome: "Warm outreach queued" },
  { from: "pricing", to: "outreach", outcome: "Sequence enrolled" },
  { from: "tech",    to: "crm",      outcome: "Account enriched" },
  { from: "hire",    to: "rep",      outcome: "Account flagged" },
  { from: "form",    to: "router",   outcome: "Rep assigned" },
  { from: "churn",   to: "cs",       outcome: "Retention triggered" },
  { from: "g2",      to: "slack",    outcome: "Follow-up queued" },
  { from: "job",     to: "crm",      outcome: "Contact updated" },
  { from: "revisit", to: "outreach", outcome: "Rep alerted" },
];

const CENTER = { x: 360, y: 360 };
const SOURCE_R = 260;
const DEST_R   = 260;
const HEX_R    = 100;      // hexagon circumradius (corner distance)

// Returns the exact radius from center to the hex edge along a given angle.
// Our hexagon has corners at (60*i - 30)° so flat-side midpoints fall at
// multiples of 60°. The distance to the edge at angle θ is apothem / cos(θ - nearestFlat).
function hexEdgeR(angleDeg, offset = 2) {
  const apothem = HEX_R * Math.cos((30 * Math.PI) / 180);
  const closest = Math.round(angleDeg / 60) * 60;
  let diff = angleDeg - closest;
  while (diff > 30) diff -= 60;
  while (diff < -30) diff += 60;
  return apothem / Math.cos((diff * Math.PI) / 180) + offset;
}

export default function PipelineArchitecture() {
  const [idx, setIdx] = useState(0);
  // Sequence modeled on the old pipeline architecture (pre-drawn lines,
  // slow dot, natural fade-in/out via opacity curve):
  //   0: idle
  //   1: discovered  — signal node fades in
  //   2: routing     — inbound line fades in, then dot fades in / travels / fades out
  //   3: processing  — engine crunches; inbound line stays visible
  //   4: dispatching — outbound line fades in, then dot fades in / travels / fades out
  //   5: delivered   — destination fades in, outcome ribbon shows, lines still visible
  //   6: clearing    — everything fades out, next route begins
  const [stage, setStage] = useState(0);
  const [log, setLog] = useState([]);
  const timers = useRef([]);

  useEffect(() => {
    const clear = () => { timers.current.forEach(clearTimeout); timers.current = []; };
    const play = () => {
      clear();
      setStage(0);
      timers.current.push(setTimeout(() => setStage(1), 400));     // discover
      timers.current.push(setTimeout(() => setStage(2), 1200));    // route (line in: 500ms, dot travel: 2000ms)
      timers.current.push(setTimeout(() => setStage(3), 3700));    // process (900ms)
      timers.current.push(setTimeout(() => setStage(4), 4600));    // dispatch (line in: 500ms, dot travel: 2000ms)
      timers.current.push(setTimeout(() => setStage(5), 7100));    // delivered (1200ms hold)
      timers.current.push(setTimeout(() => setStage(6), 8300));    // clearing (900ms)
      timers.current.push(setTimeout(() => {
        setLog((prev) => [{ ...ROUTES[idx], t: stamp() }, ...prev].slice(0, 5));
        setIdx((i) => (i + 1) % ROUTES.length);
      }, 9200));
    };
    play();
    return clear;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  const route = ROUTES[idx];
  const source = SOURCES.find((s) => s.id === route.from);
  const dest   = DESTINATIONS.find((d) => d.id === route.to);

  const status =
    stage === 0 ? { txt: "idle",        c: "#52525B" } :
    stage <= 2  ? { txt: "receiving",   c: source.color } :
    stage === 3 ? { txt: "processing",  c: "#CCFF00" } :
    stage === 4 ? { txt: "dispatching", c: dest.color } :
                  { txt: "delivered",   c: dest.color };

  return (
    <section
      id="pipeline"
      data-testid="pipeline-architecture"
      className="relative bg-[#050505] border-t border-white/10 py-24 md:py-36 overflow-hidden"
    >
      <div className="absolute inset-0 precision-grid opacity-25 pointer-events-none" />
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[700px] pointer-events-none opacity-60"
        style={{ background: `radial-gradient(ellipse, ${source.color}1a 0%, transparent 60%)`, transition: "background 0.6s" }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-8 bg-[#CCFF00]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#CCFF00]">
              The Orchestrator / 01
            </span>
          </div>
          <h2 className="font-display font-bold uppercase tracking-[-0.04em] leading-[0.9] text-white text-[36px] sm:text-[48px] md:text-[60px] lg:text-[72px] max-w-[20ch]">
            A signal becomes
            <br />
            <span className="text-[#CCFF00]">pipeline — in seconds.</span>
          </h2>
          <p className="mt-6 font-body text-zinc-400 text-[15px] md:text-[17px] leading-relaxed max-w-[58ch]">
            Every signal the market gives off — caught, routed, and dispatched
            to the right destination in your stack. Automatically.
          </p>
        </div>

        {/* Orchestrator console */}
        <div className="relative border border-white/10 bg-black/60 backdrop-blur-sm">
          <CornerMarks />

          {/* Top strip */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            <span>// svgl.orchestrator</span>
            <span className="flex items-center gap-2" style={{ color: status.c }}>
              <span
                className="h-1.5 w-1.5 rounded-full animate-pulse"
                style={{ background: status.c, boxShadow: `0 0 8px ${status.c}` }}
              />
              {status.txt}
            </span>
            <span className="opacity-0 select-none pointer-events-none">·</span>
          </div>

          {/* Main grid */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px]">
            {/* Central radial */}
            <div className="relative border-b lg:border-b-0 lg:border-r border-white/10 flex items-center justify-center p-5 min-h-[560px]">
              <Radial
                source={source}
                dest={dest}
                stage={stage}
                route={route}
              />
            </div>

            {/* Right-side panel — Right Now, oversized */}
            <div className="p-6 md:p-7 flex flex-col justify-center">
              <ActiveCard source={source} dest={dest} stage={stage} route={route} status={status} />
            </div>
          </div>

          {/* Bottom ticker */}
          <div className="border-t border-white/10 px-5 py-3 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 flex-wrap gap-3">
            <span>capture → process → dispatch</span>
            <span className="hidden md:flex items-center gap-5">
              <LegendDot c="#00F0FF" t="signal" />
              <LegendDot c="#CCFF00" t="engine" />
              <LegendDot c="#D946EF" t="action" />
            </span>
            <span className="opacity-0 select-none pointer-events-none">·</span>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───────── Active summary card ───────── */
function ActiveCard({ source, dest, stage, route, status }) {
  return (
    <div
      className="border p-6 md:p-7 relative transition-colors duration-500"
      style={{
        borderColor: stage === 0 ? "rgba(255,255,255,0.08)" : source.color + "66",
        background: stage === 0 ? "transparent" : "rgba(255,255,255,0.03)",
        boxShadow: stage === 0 ? "none" : `inset 0 0 0 1px ${source.color}22`,
      }}
      data-testid="pipeline-right-now"
    >
      {/* Corner brackets */}
      <span className="absolute -top-1 -left-1 w-3 h-3 border-l border-t" style={{ borderColor: source.color }} />
      <span className="absolute -top-1 -right-1 w-3 h-3 border-r border-t" style={{ borderColor: source.color }} />
      <span className="absolute -bottom-1 -left-1 w-3 h-3 border-l border-b" style={{ borderColor: source.color }} />
      <span className="absolute -bottom-1 -right-1 w-3 h-3 border-r border-b" style={{ borderColor: source.color }} />

      <div className="flex items-center justify-between mb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#CCFF00]">
          Right Now
        </div>
        <span
          className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em]"
          style={{ color: status.c }}
        >
          <span
            className="h-1.5 w-1.5 rounded-full animate-pulse"
            style={{ background: status.c, boxShadow: `0 0 8px ${status.c}` }}
          />
          {status.txt}
        </span>
      </div>

      <div className="space-y-5">
        <BigRow label="Signal"    value={source.label} color={source.color} on={stage >= 1 && stage <= 5} />
        <Divider />
        <BigRow label="Process"   value="SVGL engine"  color="#CCFF00"      on={stage >= 3} />
        <Divider />
        <BigRow label="Dispatch"  value={dest.label}   color={dest.color}   on={stage >= 5} />
      </div>

      <div
        className="mt-7 pt-5 border-t border-dashed border-white/10"
        style={{ opacity: stage >= 5 ? 1 : 0.35, transition: "opacity 0.5s" }}
      >
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
          Outcome
        </div>
        <div
          className="font-display font-bold uppercase tracking-tight text-lg md:text-xl leading-tight break-words"
          style={{ color: stage >= 5 ? dest.color : "rgba(255,255,255,0.3)", transition: "color 0.5s" }}
        >
          {route.outcome}.
        </div>
      </div>
    </div>
  );
}

function BigRow({ label, value, color, on }) {
  return (
    <div>
      <div className="flex items-center gap-2.5 mb-1.5">
        <span
          className="h-1.5 w-1.5 rounded-full shrink-0 transition-all duration-300"
          style={{
            background: on ? color : "rgba(255,255,255,0.12)",
            boxShadow: on ? `0 0 8px ${color}` : "none",
          }}
        />
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          {label}
        </span>
      </div>
      <div
        className="font-display font-bold uppercase tracking-tight text-lg md:text-xl leading-tight break-words transition-colors duration-300"
        style={{ color: on ? "#ffffff" : "rgba(255,255,255,0.22)" }}
      >
        {value}
      </div>
    </div>
  );
}

function Divider() {
  return <div className="h-px bg-white/5" />;
}

/* ───────── Radial diagram ───────── */
function Radial({ source, dest, stage, route }) {
  const [rot, setRot] = useState(0);
  useEffect(() => {
    let raf;
    // Very slow decorative sweep: ~3°/sec. Node visibility is no longer tied
    // to the sweep — only the currently active signal pops.
    const tick = () => { setRot((r) => (r + 0.05) % 360); raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const sigPos  = polar(CENTER, SOURCE_R, source.angle);
  const destPos = polar(CENTER, DEST_R,  dest.angle);

  // Gate points sit exactly on the hexagon's edge so the line visibly
  // touches the SVGL engine.
  const entryGate = polar(CENTER, hexEdgeR(source.angle), source.angle);
  const exitGate  = polar(CENTER, hexEdgeR(dest.angle),   dest.angle);

  return (
    <svg viewBox="0 0 720 720" className="w-full max-w-[600px] h-auto">
      <defs>
        <radialGradient id="core-halo" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%"  stopColor="#CCFF00" stopOpacity="0.55" />
          <stop offset="55%" stopColor="#CCFF00" stopOpacity="0.12" />
          <stop offset="100%" stopColor="#CCFF00" stopOpacity="0" />
        </radialGradient>
        <filter id="blur-md" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="5" />
        </filter>
      </defs>

      {/* Rotating outer tick ring */}
      <g transform={`rotate(${rot} ${CENTER.x} ${CENTER.y})`}>
        {Array.from({ length: 72 }).map((_, i) => {
          const a = (i * 5) * Math.PI / 180;
          const major = i % 6 === 0;
          const r1 = 306, r2 = major ? 316 : 310;
          return (
            <line
              key={i}
              x1={CENTER.x + r1 * Math.cos(a)}
              y1={CENTER.y + r1 * Math.sin(a)}
              x2={CENTER.x + r2 * Math.cos(a)}
              y2={CENTER.y + r2 * Math.sin(a)}
              stroke={major ? "rgba(0,240,255,0.30)" : "rgba(255,255,255,0.10)"}
              strokeWidth={major ? 1 : 0.6}
            />
          );
        })}
      </g>

      {/* Concentric rings */}
      {[170, 240, 300].map((r, i) => (
        <circle key={r} cx={CENTER.x} cy={CENTER.y} r={r}
          stroke={i === 1 ? "rgba(0,240,255,0.18)" : "rgba(255,255,255,0.05)"}
          strokeDasharray={i === 1 ? "0" : "2 5"} strokeWidth="1" fill="none" />
      ))}

      {/* Sector labels removed (visual decluttered) */}

      {/* Subtle arc paths for top and bottom halves */}
      <path d={arcPath(CENTER, SOURCE_R, -175, 20)} fill="none"
        stroke="rgba(0,240,255,0.15)" strokeWidth="1" strokeDasharray="3 4" />
      <path d={arcPath(CENTER, DEST_R, 25, 240)} fill="none"
        stroke="rgba(217,70,239,0.15)" strokeWidth="1" strokeDasharray="3 4" />

      {/* Subtle rotating radar sweep — purely decorative; doesn't reveal nodes */}
      <g transform={`rotate(${rot} ${CENTER.x} ${CENTER.y})`}>
        <defs>
          <linearGradient id="sweep-grad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor="#00F0FF" stopOpacity="0" />
            <stop offset="80%"  stopColor="#00F0FF" stopOpacity="0.04" />
            <stop offset="100%" stopColor="#00F0FF" stopOpacity="0.18" />
          </linearGradient>
        </defs>
        <path
          d={`M ${CENTER.x} ${CENTER.y} L ${CENTER.x + 300} ${CENTER.y}
              A 300 300 0 0 0 ${CENTER.x + 300 * Math.cos(-Math.PI / 6)} ${CENTER.y + 300 * Math.sin(-Math.PI / 6)} Z`}
          fill="url(#sweep-grad)"
        />
        <line
          x1={CENTER.x} y1={CENTER.y}
          x2={CENTER.x + 300} y2={CENTER.y}
          stroke="rgba(0,240,255,0.28)"
          strokeWidth="0.8"
        />
      </g>

      {/* Source node — fades in at discovery, stays through delivery, fades on clearing. */}
      {SOURCES.map((s) => {
        const p = polar(CENTER, SOURCE_R, s.angle);
        const isCurrent = s.id === source.id;
        if (!isCurrent) return <g key={s.id} opacity="0" />;
        const op = (stage >= 1 && stage <= 5) ? 1 : 0;
        return (
          <g key={s.id} opacity={op} style={{ transition: "opacity 0.7s ease-out" }}>
            <Node x={p.x} y={p.y} color={s.color} label={s.label}
              active anchor="outside-top" />
          </g>
        );
      })}

      {/* Destination node — fades in at delivery, holds through clearing. */}
      {DESTINATIONS.map((d) => {
        const p = polar(CENTER, DEST_R, d.angle);
        const isCurrent = d.id === dest.id;
        if (!isCurrent) return <g key={d.id} opacity="0" />;
        const op = stage >= 5 ? 1 : 0;
        return (
          <g key={d.id} opacity={op} style={{ transition: "opacity 0.7s ease-out" }}>
            <Node x={p.x} y={p.y} color={d.color} label={d.label}
              active anchor="outside-bottom" />
          </g>
        );
      })}

      {/* Inbound line — fades in at routing, STAYS visible through delivery,
          fades out at clearing. (Matches old pipeline: line first, then dot.) */}
      <FadeLine from={sigPos} to={entryGate} color={source.color}
        visible={stage >= 2 && stage <= 5} />

      {/* Outbound line — fades in at dispatch, stays through delivery. */}
      <FadeLine from={exitGate} to={destPos} color={dest.color}
        visible={stage >= 4 && stage <= 5} />

      {/* Core halo */}
      <circle cx={CENTER.x} cy={CENTER.y} r="130" fill="url(#core-halo)"
        opacity={stage === 3 ? 1 : 0.55} style={{ transition: "opacity 0.4s" }} />

      {/* Core hexagon */}
      <Hexagon cx={CENTER.x} cy={CENTER.y} r={HEX_R} fill="#080a05"
        stroke="#CCFF00" strokeWidth="1.5" opacity="0.9" />
      <Hexagon cx={CENTER.x} cy={CENTER.y} r={HEX_R} fill="none"
        stroke="#CCFF00" strokeWidth="1.5" />

      {/* Rotating inner ring */}
      <circle cx={CENTER.x} cy={CENTER.y} r="66" fill="none"
        stroke="rgba(204,255,0,0.3)" strokeWidth="1" strokeDasharray="3 4">
        <animateTransform attributeName="transform" type="rotate"
          from={`0 ${CENTER.x} ${CENTER.y}`} to={`360 ${CENTER.x} ${CENTER.y}`}
          dur="16s" repeatCount="indefinite" />
      </circle>

      {/* Processing bars — light up while the engine is crunching */}
      <g transform={`translate(${CENTER.x - 40} ${CENTER.y + 5})`}>
        {[0, 1, 2, 3, 4, 5, 6].map((i) => (
          <rect key={i} x={i * 12} y="0" width="6" height="8"
            fill={stage === 3 ? "#CCFF00" : "rgba(204,255,0,0.25)"}
            style={{ transition: "fill 0.2s" }}>
            {stage === 3 && (
              <animate attributeName="height" values="3;14;5;12;4;10"
                dur="0.7s" repeatCount="indefinite" begin={`${i * 0.08}s`} />
            )}
          </rect>
        ))}
      </g>

      {/* Core label */}
      <text x={CENTER.x} y={CENTER.y - 18} textAnchor="middle"
        fill="#CCFF00" fontFamily="Azeret Mono" fontWeight="bold"
        fontSize="20" letterSpacing="2">
        SVGL
      </text>

      {/* Inbound traveling dot — line has faded in already; dot now fades in,
          travels to the hex edge, fades out at the edge. */}
      {stage === 2 && (
        <TravelingDot key={`in-${route.from}-${route.to}`} from={sigPos} to={entryGate}
          color={source.color} duration={2000} startDelay={500} />
      )}

      {/* Outbound traveling dot — same treatment on the way out. */}
      {stage === 4 && (
        <TravelingDot key={`out-${route.from}-${route.to}`} from={exitGate} to={destPos}
          color={dest.color} duration={2000} startDelay={500} />
      )}

      {/* Arrival pulse at destination — blooms as the dot lands. */}
      {stage === 5 && (
        <circle cx={destPos.x} cy={destPos.y} r="6" fill="none"
          stroke={dest.color} strokeWidth="1.5">
          <animate attributeName="r" from="6" to="28" dur="0.8s" fill="freeze" />
          <animate attributeName="opacity" from="0.9" to="0" dur="0.8s" fill="freeze" />
        </circle>
      )}

      {/* Outcome ribbon — shown from delivered onward */}
      {(stage === 5 || stage === 6) && (
        <g style={{ opacity: stage === 5 ? 1 : 0, transition: "opacity 0.6s" }}>
          <rect x={CENTER.x - 150} y={CENTER.y + 150} width="300" height="26"
            fill="rgba(0,0,0,0.75)" stroke={dest.color} strokeWidth="1" />
          <text x={CENTER.x} y={CENTER.y + 167} textAnchor="middle"
            fill={dest.color} fontFamily="IBM Plex Mono" fontSize="11"
            letterSpacing="2">
            {route.outcome.toUpperCase()}
          </text>
        </g>
      )}
    </svg>
  );
}

/* ───────── Helpers ───────── */
function Hexagon({ cx, cy, r, ...rest }) {
  const points = Array.from({ length: 6 }).map((_, i) => {
    const a = (60 * i - 30) * Math.PI / 180;
    return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
  }).join(" ");
  return <polygon points={points} {...rest} />;
}

function Node({ x, y, color, label, active, ambient, anchor }) {
  const top = anchor === "outside-top";
  const ty = top ? y - 24 : y + 30;
  const baseOp = active ? 1 : (ambient ? 0.12 : 0);
  return (
    <g style={{ transition: "opacity 0.4s" }} opacity={baseOp}>
      {active && (
        <circle cx={x} cy={y} r="16" fill={color} opacity="0.2" filter="url(#blur-md)">
          <animate attributeName="r" values="12;20;12" dur="1.2s" repeatCount="indefinite" />
        </circle>
      )}
      <circle cx={x} cy={y} r="10" fill="none" stroke={color}
        strokeOpacity={active ? 0.8 : 0.3} strokeWidth="1" />
      <circle cx={x} cy={y} r={active ? 5 : 2.5} fill={color}
        style={{ filter: active ? `drop-shadow(0 0 8px ${color})` : "none", transition: "all 0.3s" }} />
      {active && (
        <text x={x} y={ty} textAnchor="middle"
          fill={color}
          fontFamily="Azeret Mono" fontWeight="bold" fontSize="12"
          letterSpacing="0.5" style={{ transition: "fill 0.3s" }}>
          {label}
        </text>
      )}
    </g>
  );
}

/* FadeLine — pre-drawn path between two points with a smooth fade-in/out on
 * the `visible` prop. Used for both inbound (signal → engine) and outbound
 * (engine → destination) lines so they appear FIRST, then the dot rides them. */
function FadeLine({ from, to, color, visible }) {
  const mx = (from.x + to.x) / 2;
  const my = (from.y + to.y) / 2;
  const dx = to.x - from.x, dy = to.y - from.y;
  const len = Math.hypot(dx, dy) || 1;
  const ox = -dy / len * 18;
  const oy =  dx / len * 18;
  const path = `M ${from.x} ${from.y} Q ${mx + ox} ${my + oy} ${to.x} ${to.y}`;
  return (
    <path d={path} stroke={color}
      strokeOpacity={visible ? 0.55 : 0}
      strokeWidth="1.3" fill="none"
      style={{ transition: "stroke-opacity 0.6s ease-out" }} />
  );
}

/* TravelingDot — a dot that rides an invisible path from `from` to `to`,
 * fading IN over the first ~15% of travel, holding, and fading OUT over the
 * last ~18% (same opacity curve used in the old pipeline architecture).
 * Optional `startDelay` lets the parent line fade in first before the dot
 * actually begins to travel. */
function TravelingDot({ from, to, color, duration = 2000, startDelay = 0 }) {
  const pathRef = useRef(null);
  const dotRef = useRef(null);
  const glowRef = useRef(null);

  const mx = (from.x + to.x) / 2;
  const my = (from.y + to.y) / 2;
  const dx = to.x - from.x, dy = to.y - from.y;
  const len = Math.hypot(dx, dy) || 1;
  const ox = -dy / len * 18;
  const oy =  dx / len * 18;
  const path = `M ${from.x} ${from.y} Q ${mx + ox} ${my + oy} ${to.x} ${to.y}`;

  useEffect(() => {
    const pathEl = pathRef.current;
    const dot = dotRef.current;
    const glow = glowRef.current;
    if (!pathEl || !dot) return;
    const totalLen = pathEl.getTotalLength();
    let raf;
    let t0 = null;
    // Smooth in-out sine ease — feels organic, not mechanical
    const ease = (t) => 0.5 - Math.cos(t * Math.PI) / 2;
    const step = (ts) => {
      if (t0 === null) t0 = ts;
      const elapsed = ts - t0 - startDelay;
      if (elapsed < 0) {
        dot.setAttribute("opacity", 0);
        if (glow) glow.setAttribute("opacity", 0);
        raf = requestAnimationFrame(step);
        return;
      }
      const t = Math.min(elapsed / duration, 1);
      const eased = ease(t);
      const pt = pathEl.getPointAtLength(eased * totalLen);
      dot.setAttribute("cx", pt.x);
      dot.setAttribute("cy", pt.y);
      // opacity curve: fade in first 15%, full 15–82%, fade out last 18%
      const op = t < 0.15 ? t / 0.15
               : t < 0.82 ? 1
               : Math.max(0, (1 - t) / 0.18);
      dot.setAttribute("opacity", op);
      if (glow) {
        glow.setAttribute("cx", pt.x);
        glow.setAttribute("cy", pt.y);
        glow.setAttribute("opacity", op * 0.45);
      }
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [path, duration, startDelay]);

  return (
    <g>
      {/* invisible path used purely for getPointAtLength() */}
      <path ref={pathRef} d={path} fill="none" stroke="none" />
      <circle ref={glowRef} cx={from.x} cy={from.y} r="14" opacity="0"
        fill={color} filter="url(#blur-md)" />
      <circle ref={dotRef} cx={from.x} cy={from.y} r="6" opacity="0"
        fill={color} style={{ filter: `drop-shadow(0 0 10px ${color})` }} />
    </g>
  );
}

function polar({ x, y }, r, deg) {
  const a = deg * Math.PI / 180;
  return { x: x + r * Math.cos(a), y: y + r * Math.sin(a) };
}

function arcPath({ x, y }, r, a1, a2) {
  const p1 = polar({ x, y }, r, a1);
  const p2 = polar({ x, y }, r, a2);
  const large = Math.abs(a2 - a1) > 180 ? 1 : 0;
  return `M ${p1.x} ${p1.y} A ${r} ${r} 0 ${large} 1 ${p2.x} ${p2.y}`;
}

function stamp() {
  const d = new Date();
  return [d.getHours(), d.getMinutes(), d.getSeconds()]
    .map((n) => String(n).padStart(2, "0")).join(":");
}

function LegendDot({ c, t }) {
  return (
    <span className="flex items-center gap-2">
      <span className="h-1 w-1 rounded-full" style={{ background: c, boxShadow: `0 0 4px ${c}` }} />
      {t}
    </span>
  );
}

function CornerMarks() {
  const cls = "absolute w-4 h-4";
  return (
    <>
      <span className={`${cls} top-0 left-0 border-l-2 border-t-2 border-[#CCFF00]`} />
      <span className={`${cls} top-0 right-0 border-r-2 border-t-2 border-[#CCFF00]`} />
      <span className={`${cls} bottom-0 left-0 border-l-2 border-b-2 border-[#CCFF00]`} />
      <span className={`${cls} bottom-0 right-0 border-r-2 border-b-2 border-[#CCFF00]`} />
    </>
  );
}
